import { Component, Host, h, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { SearchService } from '@local/services';
import { NOTIFICATION, Scope, SearchResult, SearchResultResponse } from '@local/models';
import { Emitter } from 'mitt'
import { DiscoveryAddition } from '../../../../domain/models/bus'

enum ModalState {
  Initial,
  RunningGeneralSearch,
  CompletedGeneralSearch,
  Error
}


@Component({
  tag: 'cr-universal-search-modal',
  styleUrl: 'cr-universal-search-modal.css',
  shadow: true,
})
export class CrUniversalSearchModal {

  /**
   * An application wide event bus
   */
  @Prop() applicationBus: Emitter<DiscoveryAddition>

  /**
   * The auth token to use while making calls
   * @type {string}
   */
  @Prop() authToken: string

  @State() searchResults: Array<any> = []
  @State() from: number = null
  @State() size: number = null
  @State() total: number = null
  @State() scopes: Array<Scope> = []
  @State() modalState: ModalState = ModalState.Initial

  /**
   * Show the search modal. Reset results.
   */
  @Method()
  async show() {
    this.modal.show()
    this.search.grabFocus()
    this.searchResults = []
    this.modalState = ModalState.Initial
  }

  @Method()
  async launch() {
    try {
      await this.show()
    } catch (error) {
      return Promise.reject(error)
    }
  }
  @Listen('pageChanged')
  handlePagesSelectChanged(e) {
    this.from = e.detail.start
    this.modalState = ModalState.RunningGeneralSearch
    this.searchService.search(this.searchTerm, this.scopes, this.from, this.size)
    .then((searchResult: SearchResultResponse) => {
      this.searchResults = searchResult.results
      this.modalState = ModalState.CompletedGeneralSearch
    })
  }

  @Listen('runninggeneralsearch')
  runningGeneralSearchHandler() {
    this.searchResults = []
    this.modalState = ModalState.RunningGeneralSearch
  }

  @Listen('completedgeneralsearch')
  completedGeneralSearchHandler(e: CustomEvent<any>) {
    this.setSearchResult(e.detail)
    this.modalState = ModalState.CompletedGeneralSearch
  }

  @Listen('errorfetchingscopes')
  handleErrorFetchingScopes(e: CustomEvent<any>) {
    console.error(e.detail)
    this.modalState = ModalState.Error
  }

  @Listen('discoveryClicked')
  handleDiscoveryClicked(e: CustomEvent<any>) {
    window.open(e.detail.url, "_blank")
  }

  @Listen('errorautocompletionsearch')
  handleErrorAutocompletionSearch(e: CustomEvent<any>) {
    console.log('handleErrorAutocompletionSearch')
    console.error(e)
    this.modalState = ModalState.Error
  }

  /**
   * When authToken changes, update the search service
   */
  @Watch('authToken')
  updateAuthToken() {
    this.searchService.authToken = this.authToken
  }

  render() {
    return (
      <Host>
        <cr-modal
          ref={(el) => this.modal = el as HTMLCrModalElement}
          modal-title={`Universal Search`}
        >
          <div slot="main">
            <cr-search
              ref={(el) => this.search = el as HTMLCrSearchElement}
              scopes={this.availableScopes}
              driver={this.searchService}
            />
            <div class="search-results">
              {this.renderSearchResultsTable()}
              {this.renderPaginationControls()}
            </div>
          </div>
        </cr-modal>
      </Host>
    );
  }

  private modal: HTMLCrModalElement
  private search: HTMLCrSearchElement

  private searchTerm: string = ''
  private searchService = new SearchService()

  private availableScopes = ['professional', 'lead', 'facility operator', 'facility']

  private setSearchResult = (s: SearchResult) => {
    this.from = s.from
    this.size = s.size
    this.total = s.result.total
    this.searchResults = s.result.results
    this.searchTerm = s.searchTerm
    this.scopes = s.scopes
  }

  private renderPaginationControls = () => {
    if (
      this.from === null ||
      this.size === null ||
      this.total === null ||
      this.searchResults.length === 0
    ) {
      return <div></div>
    } else {
      return(
        <cr-pagination
          pageSize={10}
          start={this.from}
          total={this.total}
        />
      )
    }
  }

  private renderSearchResultsTable = () => {
    let searchTable = <div></div>
    switch (this.modalState) {
      case ModalState.RunningGeneralSearch:
        searchTable = this.renderSearchResultsSkeleton()
        break
      case ModalState.CompletedGeneralSearch:
        if (this.searchResults.length > 0) {
          searchTable = (
            <cr-search-results
              applicationBus={this.applicationBus}
              discoveries={this.searchResults}
            />
          )
        } else {
          searchTable = (
            <cr-notification type={NOTIFICATION.INFO}>No results found.</cr-notification>
          )
        }

        break
      case ModalState.Error:
        searchTable = (
          <cr-notification type={NOTIFICATION.ERROR}>An error occured.</cr-notification>
        )
    }
    return searchTable
  }

  private renderSearchResultsSkeleton = () => {
    const skeletonRows = [...Array(5).keys()].map(() => (
      <bx-table-row>
        <bx-table-cell-skeleton></bx-table-cell-skeleton>
        <bx-table-cell-skeleton></bx-table-cell-skeleton>
        <bx-table-cell-skeleton></bx-table-cell-skeleton>
        <bx-table-cell-skeleton></bx-table-cell-skeleton>
      </bx-table-row>
      ))
    const emptyRows = [...Array(5).keys()].map(() => (
        <bx-table-row>
          <bx-table-cell></bx-table-cell>
          <bx-table-cell></bx-table-cell>
          <bx-table-cell></bx-table-cell>
          <bx-table-cell></bx-table-cell>
        </bx-table-row>
      ))
    return (
      <bx-data-table>
        <bx-table>
          <bx-table-body>
            { skeletonRows }
            { emptyRows }
          </bx-table-body>
        </bx-table>
      </bx-data-table>
    )
  }
}
